import { memo, useMemo, useState, useEffect, useRef } from 'react';
import {
    Box, Flex, Text, Image, Badge, Spinner, Button, Select,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { add_to_cart } from 'redux/actions/productActions';
import { useGetVariantById } from 'hooks/useProducts';
import PlaceHolder from 'assets/placeholder.png';
import { CartToast } from 'components/Dashboard/NewArrivalsM';
import {
    getColorSwatch,
    resolveVariantThumb,
    buildQuantityOptions,
    getVariantAxes,
    getVariantColor,
    getVariantSize,
} from './productUtils';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const SUCCESS     = '#10B981';
const SUCCESS_BG  = '#ECFDF5';
const DANGER      = '#E53E3E';

const FONT = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   COLOR SWATCH (single chip in the swatch strip)
   ───────────────────────────────────────────────────────────── */

const ColorSwatch = memo(function ColorSwatch({ color, isActive, firstVariantId, onSelect }) {
    const { data: fullVariant } = useGetVariantById(firstVariantId);
    const swatch = getColorSwatch(color);
    const isGradient = typeof swatch === 'string' && swatch.startsWith('linear-gradient');
    const img = fullVariant ? resolveVariantThumb(fullVariant) : null;

    return (
        <Box
            as="button"
            type="button"
            flexShrink={0}
            onClick={() => onSelect(color, img)}
            display="inline-flex"
            alignItems="center"
            gap="6px"
            px="8px"
            py="4px"
            borderRadius="full"
            border="1.5px solid"
            borderColor={isActive ? NAVY : BORDER}
            bg={isActive ? SURFACE : SURFACE_ALT}
            fontSize="11px"
            fontWeight="600"
            color={INK}
            fontFamily={FONT}
            cursor="pointer"
            transition="all 0.15s ease"
            _hover={{ borderColor: NAVY, transform: 'translateY(-1px)' }}
            _focusVisible={{ outline: `2px solid ${NAVY}`, outlineOffset: '2px' }}
            aria-pressed={isActive}
            aria-label={`Select ${color}`}
        >
            {swatch ? (
                <Box
                    w="12px"
                    h="12px"
                    borderRadius="full"
                    border="1px solid"
                    borderColor={BORDER}
                    bg={!isGradient ? swatch : undefined}
                    bgGradient={isGradient ? swatch : undefined}
                    flexShrink={0}
                />
            ) : (
                <Flex
                    w="12px"
                    h="12px"
                    borderRadius="full"
                    bg={BORDER}
                    align="center"
                    justify="center"
                    color={MUTED}
                    fontSize="7px"
                    fontWeight="700"
                    flexShrink={0}
                >
                    {(color?.[0] ?? '?').toUpperCase()}
                </Flex>
            )}
            {color}
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   VARIANT ROW (one row in the variant table)
   ───────────────────────────────────────────────────────────── */

const VariantRow = memo(function VariantRow({
    variant, sizeAttr, quantities, setQty, onAdd,
    setImageInView, setManualImage, itemGroupName, isJustAdded,
}) {
    const { data: fullVariant } = useGetVariantById(variant.item_id);
    const resolved = fullVariant ?? variant;

    const stock = Number(resolved.available_stock) || Number(resolved.stock_on_hand) || 0;
    const inStock = stock > 0;
    const moq = Math.max(1, Number(resolved.custom_field_hash?.cf_moq) || 1);

    /* Memoized — no recomputation on parent re-render */
    const qtyOptions = useMemo(() => buildQuantityOptions(moq, stock), [moq, stock]);

    const thumb = resolveVariantThumb(resolved);
    const size = getVariantSize(variant, sizeAttr);

    const cellStyle = {
        py: '8px',
        px: '10px',
        borderBottom: `1px solid ${BORDER}`,
        verticalAlign: 'middle',
    };

    return (
        <Flex
            as="tr"
            opacity={inStock ? 1 : 0.5}
            borderBottom={`1px solid ${BORDER}`}
            _hover={inStock ? { bg: SURFACE_ALT } : {}}
            display="table-row"
        >
            <Box as="td" {...cellStyle} display={{ base: 'none', md: 'table-cell' }}>
                {thumb ? (
                    <Image
                        src={thumb}
                        fallbackSrc={PlaceHolder}
                        w="40px"
                        h="40px"
                        objectFit="cover"
                        borderRadius="6px"
                        border="1px solid"
                        borderColor={BORDER}
                        cursor="pointer"
                        onClick={() => {
                            setImageInView(thumb);
                            setManualImage(thumb);
                        }}
                        _hover={{ borderColor: NAVY }}
                        transition="border-color 0.15s ease"
                    />
                ) : (
                    <Box w="40px" h="40px" bg={SURFACE_ALT} borderRadius="6px" />
                )}
            </Box>

            {sizeAttr && (
                <Box as="td" {...cellStyle} whiteSpace="nowrap">
                    <Text
                        display="inline-block"
                        fontSize="11px"
                        fontWeight="700"
                        px="8px"
                        py="2px"
                        bg={SURFACE_ALT}
                        borderRadius="4px"
                        color={INK}
                        border="1px solid"
                        borderColor={BORDER}
                        fontFamily={FONT}
                    >
                        {size ?? '—'}
                    </Text>
                </Box>
            )}

            <Box
                as="td"
                {...cellStyle}
                whiteSpace="nowrap"
                maxW="140px"
                display={{ base: 'none', md: 'table-cell' }}
            >
                <Text
                    fontSize="10px"
                    fontFamily="monospace"
                    color={MUTED}
                    title={resolved.sku}  /* tooltip so user can see the full SKU */
                    noOfLines={1}
                >
                    {resolved.sku ?? '—'}
                </Text>
            </Box>

            <Box as="td" {...cellStyle} whiteSpace="nowrap">
                <Badge
                    bg={`${DANGER}15`}
                    color={DANGER}
                    fontSize="9.5px"
                    fontWeight="800"
                    px="8px"
                    py="2px"
                    borderRadius="full"
                    border={`1px solid ${DANGER}30`}
                >
                    {moq}
                </Badge>
            </Box>

            <Box as="td" {...cellStyle} whiteSpace="nowrap">
                <Badge
                    bg={inStock ? `${SUCCESS}15` : `${DANGER}15`}
                    color={inStock ? SUCCESS : DANGER}
                    fontSize="9.5px"
                    fontWeight="800"
                    px="8px"
                    py="2px"
                    borderRadius="full"
                    border={`1px solid ${(inStock ? SUCCESS : DANGER)}30`}
                >
                    {inStock ? stock : 'Out'}
                </Badge>
            </Box>

            <Box as="td" {...cellStyle} whiteSpace="nowrap">
                {inStock ? (
                    <Select
                        value={quantities[variant.item_id] || moq}
                        onChange={(e) => setQty(variant.item_id, Number(e.target.value))}
                        size="xs"
                        w="64px"
                        h="28px"
                        fontSize="11px"
                        fontFamily={FONT}
                        borderColor={BORDER}
                        bg={SURFACE}
                        _hover={{ borderColor: '#CBD5E0' }}
                        _focus={{ borderColor: NAVY, boxShadow: `0 0 0 2px ${CYAN}` }}
                    >
                        {qtyOptions.map((o) => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </Select>
                ) : (
                    <Text fontSize="11px" color={FAINT}>—</Text>
                )}
            </Box>

            <Box as="td" {...cellStyle} whiteSpace="nowrap" pr="12px">
                <Button
                    isDisabled={!inStock || isJustAdded}
                    onClick={() => onAdd(resolved, thumb, itemGroupName)}
                    size="xs"
                    h="28px"
                    px="10px"
                    bg={
                        !inStock
                            ? BORDER
                            : isJustAdded
                            ? SUCCESS_BG
                            : NAVY
                    }
                    color={
                        !inStock
                            ? FAINT
                            : isJustAdded
                            ? SUCCESS
                            : 'white'
                    }
                    opacity={isJustAdded ? 0.7 : 1}
                    _hover={inStock && !isJustAdded ? { bg: NAVY_DEEP } : {}}
                    _disabled={{
                        cursor: 'not-allowed',
                        _hover: {},
                    }}
                    fontSize="11px"
                    fontWeight="700"
                    fontFamily={FONT}
                    borderRadius="6px"
                    border={isJustAdded ? `1px solid ${SUCCESS}30` : 'none'}
                    transition="all 0.25s ease"
                >
                    {!inStock ? 'Unavailable' : isJustAdded ? '✓ Added' : 'Add'}
                </Button>
            </Box>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   VARIANTS PANEL — main component
   ───────────────────────────────────────────────────────────── */

const VariantsPanel = memo(function VariantsPanel({
    itemGroup, groupLoading, setImageInView, setManualImage,
}) {
    const dispatch = useDispatch();
    const [activeColor, setActiveColor] = useState(null);
    const [quantities, setQuantities] = useState({});
    const [toast, setToast] = useState(null);
    /* Track which variant was last added and at what quantity.
       Shape: { [variantId]: lastAddedQty }
       The Add button for a variant is disabled if quantities[variantId] === lastAdded[variantId].
       Changing the quantity (or switching colors and coming back) re-enables it. */
    const [lastAdded, setLastAdded] = useState({});
    const swatchScrollRef = useRef(null);

    const items = itemGroup?.items ?? [];
    const { colorAttr, sizeAttr } = useMemo(() => getVariantAxes(itemGroup), [itemGroup]);

    /* Unique colors + the first variant ID per color (for swatch image preview) */
    const colorMeta = useMemo(() => {
        const seen = new Map();
        items.forEach((item) => {
            const c = getVariantColor(item, colorAttr);
            if (c && !seen.has(c)) seen.set(c, item.item_id);
        });
        return Array.from(seen.entries()).map(([color, firstId]) => ({ color, firstId }));
    }, [items, colorAttr]);

    const colors = useMemo(() => colorMeta.map((m) => m.color), [colorMeta]);

    /* Auto-select first color when colors load.
       Uses `colors.length` to avoid resetting on color array re-creation. */
    useEffect(() => {
        if (colors.length > 0 && !activeColor) setActiveColor(colors[0]);
    }, [colors.length, activeColor, colors]);

    const handleColorSelect = (color, img) => {
        setActiveColor(color);
        if (img) {
            setImageInView(img);
            setManualImage(img);
        }
    };

    const filteredItems = useMemo(
        () => (activeColor ? items.filter((i) => getVariantColor(i, colorAttr) === activeColor) : items),
        [items, activeColor, colorAttr]
    );

    const setQty = (itemId, qty) => {
        setQuantities((prev) => ({ ...prev, [itemId]: qty }));
        /* When qty changes, clear the last-added tracking for this variant
           so the Add button re-enables. */
        setLastAdded((prev) => {
            if (prev[itemId] === undefined) return prev;
            const { [itemId]: _, ...rest } = prev;
            return rest;
        });
    };

    const handleAdd = (resolvedVariant, thumb, groupName) => {
        const qty = quantities[resolvedVariant.item_id] || 1;
        dispatch(add_to_cart({ productId: resolvedVariant.item_id, qty }));
        /* Remember what we just added so we can disable the button until
           the user changes something. Keyed by variant ID so each row is
           tracked independently. */
        setLastAdded((prev) => ({ ...prev, [resolvedVariant.item_id]: qty }));
        setToast({
            item: {
                ...resolvedVariant,
                item_name:
                    resolvedVariant.name ??
                    resolvedVariant.item_name ??
                    groupName ??
                    itemGroup?.group_name ??
                    'Product',
            },
            image: thumb,
        });
    };

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3200);
        return () => clearTimeout(t);
    }, [toast]);

    if (groupLoading) {
        return (
            <Box
                bg={SURFACE}
                borderRadius="12px"
                p="20px"
                border="1px solid"
                borderColor={BORDER}
                boxShadow="0 1px 3px rgba(27,59,95,0.06)"
            >
                <Flex align="center" gap="8px">
                    <Spinner size="sm" color={NAVY} />
                    <Text fontSize="13px" color={MUTED} fontFamily={FONT}>
                        Loading variants…
                    </Text>
                </Flex>
            </Box>
        );
    }

    if (!items.length) return null;

    return (
        <>
            <Box
                bg={SURFACE}
                borderRadius="12px"
                border="1px solid"
                borderColor={BORDER}
                boxShadow="0 1px 3px rgba(27,59,95,0.06)"
                overflow="hidden"
            >
                {/* Header */}
                <Flex
                    px="12px"
                    py="8px"
                    justify="space-between"
                    align="center"
                    bg={SURFACE_ALT}
                    borderBottom="1px solid"
                    borderColor={BORDER}
                >
                    <Text fontSize="13px" fontWeight="800" color={INK} fontFamily={FONT}>
                        Variants
                    </Text>
                    <Text fontSize="11px" color={FAINT} fontWeight="600">
                        {items.length} variant{items.length !== 1 ? 's' : ''}
                        {colors.length > 1 && ` · ${colors.length} colors`}
                    </Text>
                </Flex>

                {/* Color swatches */}
                {colorMeta.length > 0 && (
                    <Box
                        ref={swatchScrollRef}
                        px="10px"
                        py="8px"
                        borderBottom="1px solid"
                        borderColor={BORDER}
                        overflowX="auto"
                        css={{
                            '&::-webkit-scrollbar': { height: '4px' },
                            '&::-webkit-scrollbar-thumb': {
                                background: BORDER,
                                borderRadius: '4px',
                            },
                        }}
                    >
                        <Flex gap="6px" flexWrap="nowrap" width="max-content">
                            {colorMeta.map(({ color, firstId }) => (
                                <ColorSwatch
                                    key={color}
                                    color={color}
                                    isActive={activeColor === color}
                                    firstVariantId={firstId}
                                    onSelect={handleColorSelect}
                                />
                            ))}
                        </Flex>
                    </Box>
                )}

                {/* Variant table */}
                <Box
                    overflowY="auto"
                    maxH="340px"
                    css={{
                        '&::-webkit-scrollbar': { width: '5px' },
                        '&::-webkit-scrollbar-thumb': {
                            background: BORDER,
                            borderRadius: '4px',
                        },
                    }}
                >
                    <Box
                        as="table"
                        width="100%"
                        sx={{
                            borderCollapse: 'collapse',
                            fontSize: '12px',
                            tableLayout: 'auto',
                            fontFamily: FONT,
                        }}
                    >
                        <Box
                            as="thead"
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                background: SURFACE_ALT,
                            }}
                        >
                            <Box as="tr">
                                {/* Build the headers array with mobile-visibility marker.
                                   Items shaped as { label, hideOnMobile } so the SKU column
                                   can be hidden on small screens while the row data is the
                                   same array shape. */}
                                {[
                                    { label: '', hideOnMobile: true  },
                                    sizeAttr && { label: 'Size' },
                                    { label: 'SKU', hideOnMobile: true },
                                    { label: 'MOQ' },
                                    { label: 'Stock' },
                                    { label: 'Qty' },
                                    { label: '' },
                                ]
                                    .filter(Boolean)
                                    .map((h, i) => (
                                        <Box
                                            as="th"
                                            key={`h-${i}`}
                                            textAlign="left"
                                            py="6px"
                                            px="8px"
                                            fontSize="10px"
                                            fontWeight="800"
                                            color={FAINT}
                                            letterSpacing="0.06em"
                                            textTransform="uppercase"
                                            whiteSpace="nowrap"
                                            borderBottom={`1px solid ${BORDER}`}
                                            display={h.hideOnMobile ? { base: 'none', md: 'table-cell' } : 'table-cell'}
                                        >
                                            {h.label}
                                        </Box>
                                    ))}
                            </Box>
                        </Box>
                        <Box as="tbody">
                            {filteredItems.map((variant) => {
                                const currentQty = quantities[variant.item_id];
                                const lastAddedQty = lastAdded[variant.item_id];
                                /* "Just added" = lastAddedQty is recorded AND
                                   matches the current selected qty.
                                   We also handle the default-MOQ case: if user
                                   hasn't touched the qty selector after adding,
                                   currentQty may still be the MOQ default which
                                   IS the qty that was added. */
                                const effectiveQty = currentQty ?? lastAddedQty;
                                const isJustAdded =
                                    lastAddedQty !== undefined &&
                                    effectiveQty === lastAddedQty;
                                return (
                                    <VariantRow
                                        key={variant.item_id}
                                        variant={variant}
                                        sizeAttr={sizeAttr}
                                        quantities={quantities}
                                        setQty={setQty}
                                        onAdd={handleAdd}
                                        setImageInView={setImageInView}
                                        setManualImage={setManualImage}
                                        itemGroupName={itemGroup?.group_name}
                                        isJustAdded={isJustAdded}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {toast && (
                <CartToast item={toast.item} image={toast.image} onClose={() => setToast(null)} />
            )}
        </>
    );
});

export default VariantsPanel;